import { createSettingsParams, SettingsParamType } from '@wix/tpa-settings';
import { CommentBoxPositionOptions, CommentSortOptions } from '~common/common-types';
import { AdditionalReactions } from './Settings/settings-tab/reactions-section/additional-reactions.const';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';

export type ISettingsParams = {
  settingsUpdateTimestamp: SettingsParamType.String;
  isDisplayMobileOn: SettingsParamType.Boolean;
  isCommentsTitleEnabled: SettingsParamType.Boolean;
  isCommentsCountEnabled: SettingsParamType.Boolean;
  isDividerEnabled: SettingsParamType.Boolean;
  isSortingEnabled: SettingsParamType.Boolean;
  sortCommentsBy: CommentSortOptions;
  commentBoxPosition: CommentBoxPositionOptions;
  isAvatarsEnabled: SettingsParamType.Boolean;
  isPublishDateEnabled: SettingsParamType.Boolean;
  isMembersBadgeEnabled: SettingsParamType.Boolean;
  isReplyingToMarkingEnabled: SettingsParamType.Boolean;
  isImagePluginEnabled: SettingsParamType.Boolean;
  isVideoPluginEnabled: SettingsParamType.Boolean;
  isGiphyPluginEnabled: SettingsParamType.Boolean;
  isEmojiPluginEnabled: SettingsParamType.Boolean;
  isFileUploadPluginEnabled: SettingsParamType.Boolean;
  isPollPluginEnabled: SettingsParamType.Boolean;
  isDividerPluginEnabled: SettingsParamType.Boolean;
  isHtmlCodePluginEnabled: SettingsParamType.Boolean;
  isGalleryPluginEnabled: SettingsParamType.Boolean;
  isCodeBlockPluginEnabled: SettingsParamType.Boolean;
  isCollapsibleListPluginEnabled: SettingsParamType.Boolean;
  isTablePluginEnabled: SettingsParamType.Boolean;
  isStoresPluginEnabled: SettingsParamType.Boolean;
  isEventsPluginEnabled: SettingsParamType.Boolean;
  isBookingsPluginEnabled: SettingsParamType.Boolean;
  isYouTubePluginEnabled: SettingsParamType.Boolean;
  isSoundCloudPluginEnabled: SettingsParamType.Boolean;
  isSpotifyPluginEnabled: SettingsParamType.Boolean;
  isInstagramPluginEnabled: SettingsParamType.Boolean;
  isTwitterPluginEnabled: SettingsParamType.Boolean;
  isTikTokPluginEnabled: SettingsParamType.Boolean;
  isTextEditingEnabled: SettingsParamType.Boolean;
  isAddingLinksEnabled: SettingsParamType.Boolean;
  commentsOnFirstPage: SettingsParamType.Number;
  commentsOnLoadMore: SettingsParamType.Number;
  repliesOnFirstPage: SettingsParamType.Number;
  repliesOnLoadMore: SettingsParamType.Number;
  isCommentsTitleEnabledMobile: SettingsParamType.Boolean;
  isCommentsCountEnabledMobile: SettingsParamType.Boolean;
  isDividerEnabledMobile: SettingsParamType.Boolean;
  isSortingEnabledMobile: SettingsParamType.Boolean;
  isAvatarsEnabledMobile: SettingsParamType.Boolean;
  isPublishDateEnabledMobile: SettingsParamType.Boolean;
  isMembersBadgeEnabledMobile: SettingsParamType.Boolean;
  isReplyingToMarkingEnabledMobile: SettingsParamType.Boolean;
  isCommentingLocked: SettingsParamType.Boolean;
  headerTitle: SettingsParamType.String;
  placeholderText: SettingsParamType.String;
  placeholderTextReply: SettingsParamType.String;
  publishButtonText: SettingsParamType.String;
  publishButtonTextReply: SettingsParamType.String;
  likeButtonText: SettingsParamType.String;
  replyButtonText: SettingsParamType.String;
  ratingSelectorText: SettingsParamType.String;
  isHeaderLoginEnabled: SettingsParamType.Boolean;
  additionalReactions: { reactions: AdditionalReactions[] };
  isAdminBadgeEnabled: SettingsParamType.Boolean;
  adminBadgeText: SettingsParamType.String;
  guestUsernameRequired: SettingsParamType.Boolean;
  guestEmailRequired: SettingsParamType.Boolean;
};

export default createSettingsParams<ISettingsParams>({
  settingsUpdateTimestamp: {
    type: SettingsParamType.String,
    getDefaultValue: () => new Date().toISOString(),
    metadata: {
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: new Date().toISOString(),
      },
    },
  },
  isDisplayMobileOn: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isCommentsTitleEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isCommentsCountEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isCommentsTitleEnabledMobile: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isCommentsCountEnabledMobile: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isDividerEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isDividerEnabledMobile: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isSortingEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isSortingEnabledMobile: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  sortCommentsBy: {
    getDefaultValue: () => CommentSortOptions.newest,
    inheritDesktop: true,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: CommentSortOptions.newest,
      },
    },
  },
  commentBoxPosition: {
    getDefaultValue: () => CommentBoxPositionOptions.auto,
    inheritDesktop: true,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: CommentBoxPositionOptions.auto,
      },
    },
  },
  isAvatarsEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isPublishDateEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isMembersBadgeEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isReplyingToMarkingEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isAvatarsEnabledMobile: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isPublishDateEnabledMobile: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isMembersBadgeEnabledMobile: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isReplyingToMarkingEnabledMobile: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isImagePluginEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isVideoPluginEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isGiphyPluginEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isEmojiPluginEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isFileUploadPluginEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isPollPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isDividerPluginEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isHtmlCodePluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isGalleryPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isCodeBlockPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isCollapsibleListPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isTablePluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isStoresPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isEventsPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isBookingsPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isYouTubePluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isSoundCloudPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isSpotifyPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isInstagramPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isTwitterPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isTikTokPluginEnabled: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  isTextEditingEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  isAddingLinksEnabled: {
    getDefaultValue: () => true,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  commentsOnFirstPage: {
    getDefaultValue: () => 10,
    inheritDesktop: true,
    metadata: {
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: 10,
      },
    },
  },
  commentsOnLoadMore: {
    getDefaultValue: () => 10,
    inheritDesktop: true,
    metadata: {
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: 10,
      },
    },
  },
  repliesOnFirstPage: {
    getDefaultValue: () => 2,
    inheritDesktop: true,
    metadata: {
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: 2,
      },
    },
  },
  repliesOnLoadMore: {
    getDefaultValue: () => 5,
    inheritDesktop: true,
    metadata: {
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: 5,
      },
    },
  },
  isCommentingLocked: {
    getDefaultValue: () => false,
    inheritDesktop: true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  headerTitle: {
    type: SettingsParamType.String,
    getDefaultValue: ({ t }) => t('settings.textTab.headerTitleTextDefault'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('settings.textTab.headerTitleTextDefault'),
      },
    }),
  },
  placeholderText: {
    type: SettingsParamType.String,
    getDefaultValue: ({ t }) => t('settings.textTab.placeholderTextDefault'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('settings.textTab.placeholderTextDefault'),
      },
    }),
  },
  placeholderTextReply: {
    type: SettingsParamType.String,
    getDefaultValue: ({ t }) => t('settings.textTab.placeholderTextDefault'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('settings.textTab.placeholderTextDefault'),
      },
    }),
  },
  publishButtonText: {
    type: SettingsParamType.String,
    getDefaultValue: ({ t }) => t('settings.textTab.publishButtonTextDefault'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('settings.textTab.publishButtonTextDefault'),
      },
    }),
  },
  publishButtonTextReply: {
    type: SettingsParamType.String,
    getDefaultValue: ({ t }) => t('settings.textTab.publishButtonTextDefault'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('settings.textTab.publishButtonTextDefault'),
      },
    }),
  },
  likeButtonText: {
    type: SettingsParamType.String,
    getDefaultValue: ({ t }) => t('settings.textTab.likeButtonTextDefault'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('settings.textTab.likeButtonTextDefault'),
      },
    }),
  },
  replyButtonText: {
    type: SettingsParamType.String,
    getDefaultValue: ({ t }) => t('settings.textTab.replyButtonTextDefault'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('settings.textTab.replyButtonTextDefault'),
      },
    }),
  },
  ratingSelectorText: {
    type: SettingsParamType.String,
    getDefaultValue: ({ t }) => t('settings.settingsTab.ratingSelectorTextDefault'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('settings.settingsTab.ratingSelectorTextDefault'),
      },
    }),
  },
  isHeaderLoginEnabled: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  additionalReactions: {
    getDefaultValue: () => ({ reactions: [] }),
    metadata: {
      type: DataType.schema,
      defaultValue: {
        type: 'static',
        value: { reactions: [] },
      },
    },
  },
  isAdminBadgeEnabled: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  adminBadgeText: {
    type: SettingsParamType.String,
    getDefaultValue: ({ t }) => t('settings.textTab.adminBadgeTextDefault'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('settings.textTab.adminBadgeTextDefault'),
      },
    }),
  },
  guestUsernameRequired: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  guestEmailRequired: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
});
